import { ApplicationView, useController } from '@aha-app/mvc';
import classNames from 'classnames';
import { usePersistedState } from '@aha-app/aha-shared';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  useCallback,
  type ComponentType,
} from 'react';
import { HexColorPicker } from 'react-colorful';
import styles from '../styles.module.css';
import ToolButton from '../toolbars/ToolButton';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolbarArea from '../toolbars/ToolbarArea';
import ToolbarVDivider from '../toolbars/ToolbarVDivider';
import { isRightClick } from '../utils/click';
import ToolbarColumn from '../toolbars/ToolbarColumn';
import WithHint from '../toolbars/WithHint';
import opacitySVG from '../assets/icons/opacity.svg';
import { svgToIcon } from '../utils/svg';
import WhiteboardController from '../controllers/WhiteboardController';
import Tool from './Tool';
import {
  cleanupColor,
  cleanupColorArray,
  customColorKey,
  isTransparent,
} from '../utils/colorUtils';

interface ColorPaletteProps {
  palette: string[];
  hollow?: boolean;
  currentColor: string;
  setCurrentColor: (color: string) => void;
  hidePopup: () => void;
  children?: React.ReactNode;
  onColorHover?: (color: string | null) => void;
}

export const ColorPalette = ApplicationView<ComponentType<ColorPaletteProps>>(
  ({
    palette,
    hollow = false,
    currentColor,
    setCurrentColor,
    hidePopup,
    children,
    onColorHover,
  }) => {
    const controller = useController(WhiteboardController);
    const commitChanges = useCallback(
      () => Tool.toolActionComplete({ controller }),
      [controller]
    );

    const [selectingCustom, setSelectingCustom] = useState(false);
    currentColor = cleanupColor(currentColor);
    const [colorValue, setValueColor] = useState(currentColor);

    // Commit changes when component unmounts, i.e. user clicked canvas
    useEffect(() => () => commitChanges(), [commitChanges]);

    const setColor = useCallback(
      (color: string, stayOpen = false, commit = true, keepFocus = false) => {
        setCurrentColor(color);
        if (!keepFocus) {
          controller.actionFocus(false);
        }
        if (!stayOpen) hidePopup();
        if (commit) {
          commitChanges();
        } else {
          controller.canvas.requestRenderAll();
        }
      },
      [controller, commitChanges, hidePopup, setCurrentColor]
    );

    const colorChanged = (color: string, commit = true, keepFocus = false) => {
      setValueColor(color);
      color = cleanupColor(color);
      setColor(color, true, commit, keepFocus);
    };

    const [customColors, setCustomColors] = usePersistedState(
      customColorKey,
      cleanupColorArray,
      true
    );

    // Save the current color (if custom) on close. Note this relies on the useEffect
    // dependencies being stable, so the hook cleanup function only fires on unmount.
    // Use refs so the callback can access the current value without depending on it, and as
    // a defensive measure against accidental unstable props
    const currentColorRef = useRef<string | undefined>();
    currentColorRef.current = currentColor;
    const paletteRef = useRef<string[]>();
    paletteRef.current = palette;
    useEffect(() => {
      return () => {
        const selected = currentColorRef.current;
        if (selected) {
          setCustomColors(prevColors => {
            if (
              !selected ||
              paletteRef.current?.includes(selected) ||
              prevColors.includes(selected)
            ) {
              return prevColors;
            }

            return prevColors.concat([selected]).slice(-20);
          });
        }
      };
    }, [setCustomColors]);

    // sometimes react-colorful's internal state seems to get out of sync, this allows us to reset it when changing the
    // value externally
    const [colorPickerKey, setColorPickerKey] = useState(Math.random());

    const eyeDropperSupported = !!window.EyeDropper;
    const openEyeDropper = () => {
      if (!window.EyeDropper) {
        console.error('EyeDropper not supported');
        return;
      }

      const eyeDropper = new window.EyeDropper();

      eyeDropper
        .open()
        .then(result => {
          const color = result.sRGBHex;
          colorChanged(color.toUpperCase());
          setColorPickerKey(Math.random());
        })
        .catch(e => {
          console.error(e);
        });
    };

    return (
      <ToolbarMenu
        columns={4}
        scrollEnabled={!selectingCustom}
        allowScroll={true}
      >
        {children}

        {!selectingCustom && (
          <ToolbarColumn>
            <div className={styles.colorSwatches}>
              {palette.map((color, i) => (
                <ColorSwatch
                  key={i}
                  color={color}
                  clickAction={setColor}
                  selected={currentColor === color}
                  swatchIsInToolbar={false}
                  hollow={hollow}
                  onMouseEnter={onColorHover}
                  onMouseLeave={
                    onColorHover ? () => onColorHover(null) : undefined
                  }
                />
              ))}
            </div>

            {customColors.length ? <ToolbarVDivider /> : null}
            <div className={styles.colorSwatches}>
              {customColors.map((color, i) => (
                <ColorSwatch
                  key={i}
                  color={color}
                  clickAction={setColor}
                  selected={currentColor === color}
                  removeColor={() => {
                    setCustomColors(prevColors =>
                      prevColors.filter(c => c !== color)
                    );
                    // if removing the currently selected color, move selection to previous
                    if (color === currentColor) {
                      colorChanged(
                        i > 0
                          ? customColors[i - 1]
                          : palette[palette.length - 1]
                      );
                    }
                  }}
                  swatchIsInToolbar={false}
                  hollow={hollow}
                  onMouseEnter={onColorHover}
                  onMouseLeave={
                    onColorHover ? () => onColorHover(null) : undefined
                  }
                />
              ))}

              <ToolButton onClick={() => setSelectingCustom(true)}>
                <aha-icon icon='fas fa-plus' />
              </ToolButton>
            </div>
          </ToolbarColumn>
        )}
        {selectingCustom && (
          <>
            <div
              // react-colorful doesn't have a "change end" handler, so we work around it as per suggestion here
              // https://github.com/omgovich/react-colorful/issues/187
              onMouseUp={commitChanges}
              style={{ display: 'flex', width: '100%' }}
            >
              <HexColorPicker
                key={colorPickerKey}
                color={currentColor || '#FFFFFF'}
                onChange={color => colorChanged(color.toUpperCase(), false)}
              />
            </div>
            <div className='flex-row'>
              <input
                className={styles.colorPickerHexInput}
                value={colorValue}
                onChange={e => colorChanged(e.target.value, false, true)}
                onBlur={() => {
                  commitChanges();
                }}
              />

              {eyeDropperSupported && (
                <a
                  onClick={openEyeDropper}
                  className={classNames(styles.eyedropperIconLink)}
                >
                  <aha-icon icon='fa-regular fa-eye-dropper' />
                </a>
              )}
            </div>
          </>
        )}
      </ToolbarMenu>
    );
  }
);

interface ColorSwatchProps {
  color: string;
  clickAction?: (color: string) => void;
  selected?: boolean;
  hollow?: boolean;
  swatchIsInToolbar: boolean;
  removeColor?: (color: string) => void;
  opacity?: number;
  onMouseEnter?: (color: string) => void;
  onMouseLeave?: () => void;
}

const ColorSwatch: React.FC<ColorSwatchProps> = ({
  color,
  clickAction,
  selected,
  hollow = false,
  swatchIsInToolbar,
  removeColor,
  opacity = 1,
  onMouseEnter,
  onMouseLeave,
}) => {
  const contextMenuRef = useRef<{ show: () => void }>(null);
  const remove = useCallback(() => removeColor?.(color), [color, removeColor]);

  const colorIsTransparent = isTransparent(color);
  let colorStyles;
  if (colorIsTransparent) {
    colorStyles = undefined;
  } else if (hollow) {
    colorStyles = { borderColor: color };
  } else {
    colorStyles = { backgroundColor: color };
  }

  const dataAttribute = swatchIsInToolbar
    ? {
        'data-test-toolbar-swatch-color': colorIsTransparent
          ? 'transparent'
          : color,
      }
    : { 'data-testid': `Colorswatch-${color}` };

  return (
    <div
      className={classNames(styles.colorSwatchContainer)}
      {...dataAttribute}
      // This is the same logic we use in ToolButton to avoid stealing focus from
      // the text editor
      onMouseDown={e => e.preventDefault()}
    >
      <div
        className={classNames(styles.colorSwatchInner, {
          [styles.colorSwatchSelected]: selected,
        })}
        onMouseUp={e => {
          if (isRightClick(e)) {
            contextMenuRef.current?.show();
            e.preventDefault();
          }
        }}
        onClick={() => {
          if (clickAction) {
            clickAction(color);
          }
        }}
        onMouseEnter={() => {
          onMouseEnter?.(color);
        }}
        onMouseLeave={() => {
          onMouseLeave?.();
        }}
      >
        <div
          className={classNames(styles.colorSwatch, {
            [styles.blankPattern]: colorIsTransparent,
          })}
        >
          {!colorIsTransparent && (
            <>
              <div
                className={classNames(styles.colorSwatchColorBackground, {
                  [styles.colorSwatchWhite]: color === '#FFFFFF',
                  [styles.colorSwatchColorBackgroundHollow]: hollow,
                })}
              />
              <div
                className={classNames(styles.colorSwatchColorRight, {
                  [styles.colorSwatchWhite]: color === '#FFFFFF',
                  [styles.colorSwatchHollow]: hollow,
                })}
                style={{ ...colorStyles, opacity }}
              />
              <div
                className={classNames(styles.colorSwatchColorLeft, {
                  [styles.colorSwatchWhite]: color === '#FFFFFF',
                  [styles.colorSwatchHollow]: hollow,
                })}
                style={colorStyles}
              />
            </>
          )}
          {colorIsTransparent && hollow && (
            <div className={classNames(styles.innerColorSwatch)} />
          )}
        </div>
      </div>
      {removeColor ? (
        <CustomColorMenu ref={contextMenuRef} remove={remove} />
      ) : null}
    </div>
  );
};

export default ApplicationView<ComponentType<ColorSwatchProps>>(ColorSwatch);

interface CustomColorMenuProps {
  remove: () => void;
}
// eslint-disable-next-line react/display-name
const CustomColorMenu = forwardRef(({ remove }: CustomColorMenuProps, ref) => {
  const menuRef = useRef<HTMLAhaMenuElement>();
  const [visible, setVisible] = useState(false);

  // expose a `show` method to the parent via ref
  useImperativeHandle(
    ref,
    () => {
      return {
        show() {
          setVisible(true);
          menuRef.current?.setAttribute('open', 'true');
        },
      };
    },
    []
  );

  if (!visible) {
    return null;
  }

  return (
    <aha-menu
      ref={menuRef}
      open={true}
      style={{
        position: 'absolute',
        left: '50%',
        top: '50%',
      }}
    >
      <div slot='control' />
      <aha-menu-content>
        <aha-menu-item
          onClick={() => {
            remove();
          }}
        >
          <a className={styles.menuItemWithShortcut}>
            <span className={styles.menuItemName}>Remove color</span>
          </a>
        </aha-menu-item>
      </aha-menu-content>
    </aha-menu>
  );
});

interface OpacitySliderProps {
  ticks?: string;
  opacity: number | undefined;
  handleOpacityChange: (opacity: number) => void;
}

export const OpacitySlider: React.FC<OpacitySliderProps> = ({
  ticks = '[10,20,30,40,50,60,70,80,90,100]',
  opacity,
  handleOpacityChange,
}) => {
  const controller = useController(WhiteboardController);

  return (
    <ToolbarArea>
      <WithHint name='Opacity'>{svgToIcon(opacitySVG)}</WithHint>
      <aha-slider
        data-testId='opacitySlider'
        ticks={ticks}
        start-value={(opacity ?? 1) * 100}
        onInput={event => {
          // @ts-expect-error selectedTickValue is not typed here
          const value: number = event.target.selectedTickValue;

          handleOpacityChange(value / 100);
          controller.canvas.requestRenderAll();
        }}
        onMouseUp={() => Tool.toolActionComplete({ controller })}
      />
    </ToolbarArea>
  );
};
